'use client';

import { useTranslations } from 'next-intl';
import { SITE } from '@/app/constants';
import { CommunityEnum, RecommendSubTab } from '@/app/types';
import { cn, reportEvent } from '@/app/utils';
import { useRouter } from '@/i18n/routing';
import { FeedContainer } from '../_components/FeedContainer';

const RECOMMEND_SUB_TAB_CONFIG: Record<RecommendSubTab, { exploreTab: CommunityEnum; mixed: boolean }> = {
  [RecommendSubTab.All]: { exploreTab: CommunityEnum.MediaAsset, mixed: true },
  [RecommendSubTab.Inspiration]: { exploreTab: CommunityEnum.MediaAsset, mixed: false },
  [RecommendSubTab.ShortFilm]: { exploreTab: CommunityEnum.ShortFilm, mixed: false },
  [RecommendSubTab.Tutorial]: { exploreTab: CommunityEnum.Tutorial, mixed: false },
};

const RECOMMEND_SUB_TAB_ROUTES: Record<RecommendSubTab, string> = {
  [RecommendSubTab.All]: '/home/recommend',
  [RecommendSubTab.Inspiration]: '/home/inspiration',
  [RecommendSubTab.ShortFilm]: '/home/short-film',
  [RecommendSubTab.Tutorial]: '/home/tutorial',
};

const STICKY_SUB_TAB_CLASS = 'sticky top-9 z-[15] bg-[#0A0D11] py-3';

interface RecommendPageClientProps {
  activeSubTab?: RecommendSubTab;
}

export const RecommendPageClient = ({ activeSubTab = RecommendSubTab.All }: RecommendPageClientProps) => {
  const t = useTranslations();
  const router = useRouter();

  if (SITE !== 'china') {
    return <FeedContainer exploreTab={CommunityEnum.MediaAsset} mixed={true} />;
  }

  const subTabs = [
    { key: RecommendSubTab.All, label: t('homeTab.all') },
    { key: RecommendSubTab.Inspiration, label: t('homeTab.mediaAsset') },
    { key: RecommendSubTab.ShortFilm, label: t('homeTab.shortFilm') },
    { key: RecommendSubTab.Tutorial, label: t('homeTab.tutorial') },
  ];

  const { exploreTab, mixed } = RECOMMEND_SUB_TAB_CONFIG[activeSubTab];
  const hasInternalFilter = activeSubTab !== RecommendSubTab.All;

  return (
    <>
      <div className={cn(STICKY_SUB_TAB_CLASS, 'flex items-center justify-start gap-6 text-base leading-[20px]')}>
        {subTabs.map((tab) => (
          <div
            key={tab.key}
            className={cn(
              'flex flex-shrink-0 cursor-pointer flex-col font-normal text-[14px] text-system-white64 sm:text-sm',
              activeSubTab === tab.key && 'font-medium text-white',
            )}
            onClick={() => {
              if (tab.key !== activeSubTab) {
                router.push(RECOMMEND_SUB_TAB_ROUTES[tab.key]);
              }
              reportEvent('home_recommend_subtab_click', {
                tab_name: tab.key,
              });
            }}
          >
            {tab.label}
          </div>
        ))}
      </div>
      <FeedContainer
        exploreTab={exploreTab}
        mixed={mixed}
        stickyFilterTopClassName={hasInternalFilter ? 'top-[80px]' : undefined}
        hideInspirationTypeFilter={activeSubTab === RecommendSubTab.Inspiration}
      />
    </>
  );
};
